import { useCallback, useState } from "react";
import { mhvillageApi } from "api/mhvillage";
import type { MhvillageExternalMapping } from "api/mhvillage";
import { useMutation, useQuery } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { Layout } from "providers/Layout";
import { useAuthContext } from "providers/Auth";
import { Button, ButtonSize, ButtonVariant, Show } from "shared/components";
import { BreadCrumbs } from "shared/components/BreadCrumbs";
import { Card } from "shared/components/Card";
import { Unlink02Icon, Upload01Icon } from "@hugeicons-pro/core-stroke-rounded";
import { IntegrationLogoIcon } from "views/settings-integration/components/IntegrationLogoIcon";
import { IntegrationId } from "views/settings-integration/components/AddIntegrationModal/types";
import { toast } from "react-toastify";
import { getErrorMessage } from "utils/error";
import { useUnitsPropertiesDictionary } from "shared/hooks/use-units-properties";
import { useExternalMappings } from "./api/hooks";
import { CommunityMappingTable } from "./components/CommunityMappingTable";
import { MappingModal } from "./components/MappingModal";
import { DeleteMappingModal } from "./components/DeleteMappingModal";
import { ImportCsvModal } from "./components/ImportCsvModal";

export const MHVillageView = () => {
  const navigate = useNavigate();
  const { refreshPartnerData } = useAuthContext();
  const { Property, Properties } = useUnitsPropertiesDictionary();

  const [modalMode, setModalMode] = useState<"create" | "edit" | null>(null);
  const [mappingToEdit, setMappingToEdit] =
    useState<MhvillageExternalMapping | null>(null);
  const [mappingToDelete, setMappingToDelete] =
    useState<MhvillageExternalMapping | null>(null);
  const [isImportOpen, setIsImportOpen] = useState(false);

  const {
    data: status,
    isLoading,
    isError
  } = useQuery({
    queryKey: ["MHVILLAGE", "STATUS"],
    queryFn: mhvillageApi.getStatus
  });

  const {
    data: mappings = [],
    isLoading: isLoadingMappings,
    isError: isMappingsError
  } = useExternalMappings();

  const { mutate: disconnect, isPending: isDisconnecting } = useMutation({
    mutationKey: ["MHVILLAGE", "DELETE"],
    mutationFn: () => mhvillageApi.disconnect(),
    onSuccess: () => {
      toast.success("MHVillage integration disconnected successfully");
      refreshPartnerData();
      navigate("/settings/integration");
    },
    onError: (err) => {
      toast.error(getErrorMessage(err));
    }
  });

  // Stable identity so it doesn't invalidate CommunityMappingTable's columns useMemo.
  const handleEdit = useCallback((mapping: MhvillageExternalMapping) => {
    setMappingToEdit(mapping);
    setModalMode("edit");
  }, []);

  const handleCloseModal = () => {
    setModalMode(null);
    setMappingToEdit(null);
  };

  return (
    <Layout
      header={
        <>
          <div className="flex items-center justify-between border-b border-alpha-40 px-3 md:px-6 pb-3">
            <BreadCrumbs
              crumbs={[
                { title: "Settings", href: "/settings" },
                { title: "Integration", href: "/settings/integration" },
                {
                  title: "MHVillage",
                  href: "/settings/integration/mhvillage"
                }
              ]}
            />
          </div>
          <div className="h-[88px] flex items-center justify-between px-6 py-4 border-b border-alpha-40">
            <div className="flex items-center gap-4">
              <IntegrationLogoIcon id={IntegrationId.MHVILLAGE} size="SM" />
              <p className="text-xl font-bold text-content-1000">MHVillage</p>
            </div>
            <Button
              variant={ButtonVariant.BASE}
              size={ButtonSize.SM}
              HugeIcon={Unlink02Icon}
              onClick={() => disconnect()}
              loading={isDisconnecting}
            >
              Disconnect
            </Button>
          </div>
          <div className="px-6 h-[76px] flex flex-col justify-center gap-2 text-sm">
            <p className="text-content-400">Integration type</p>
            <p className="font-medium text-content-1000">MHVillage</p>
          </div>
        </>
      }
    >
      <div className="space-y-6">
        <Card className="w-full p-6">
          <p className="text-sm text-content-400">Parsing email</p>
          <Show if={isError}>
            <p className="mt-1 text-base text-content-1000">
              Unable to load the parsing email. Please try again later.
            </p>
          </Show>
          <Show if={!isError}>
            <p className="mt-2 text-base font-bold text-content-1000">
              {isLoading ? "Loading…" : (status?.parsing_email ?? "—")}
            </p>
          </Show>
        </Card>

        <Card className="w-full p-6">
          <div className="flex items-start justify-between gap-4">
            <div className="flex-1">
              <p className="text-base font-bold text-content-1000">
                {Property} mapping
              </p>
              <p className="mt-1 text-sm text-content-400">
                Match MH Village External IDs to your Boom{" "}
                {Properties.toLowerCase()}. Leads from MH Village will be routed
                to the mapped {Property.toLowerCase()} based on the below.
              </p>
            </div>
            <div className="flex items-center gap-2 shrink-0">
              <Button
                variant={ButtonVariant.BASE}
                size={ButtonSize.SM}
                HugeIcon={Upload01Icon}
                onClick={() => setIsImportOpen(true)}
              >
                Import CSV
              </Button>
              <Button
                variant={ButtonVariant.PRIMARY}
                size={ButtonSize.SM}
                onClick={() => setModalMode("create")}
              >
                Add record
              </Button>
            </div>
          </div>

          <div className="mt-6">
            <Show if={isMappingsError}>
              <p className="py-8 text-center text-base text-content-1000">
                Unable to load mappings. Please try again later.
              </p>
            </Show>
            <Show if={!isMappingsError}>
              <CommunityMappingTable
                mappings={mappings}
                isLoading={isLoadingMappings}
                communityLabel={Property}
                communitiesLabel={Properties}
                onEdit={handleEdit}
                onDelete={setMappingToDelete}
              />
            </Show>
          </div>
        </Card>
      </div>

      <Show if={modalMode !== null}>
        <MappingModal
          key={mappingToEdit?.id ?? "create"}
          opened={modalMode !== null}
          mode={modalMode ?? "create"}
          mapping={mappingToEdit}
          communityLabel={Property}
          onClose={handleCloseModal}
        />
      </Show>

      <DeleteMappingModal
        opened={mappingToDelete !== null}
        mapping={mappingToDelete}
        onClose={() => setMappingToDelete(null)}
      />

      <ImportCsvModal
        opened={isImportOpen}
        communityLabel={Property}
        communitiesLabel={Properties}
        mappings={mappings}
        onClose={() => setIsImportOpen(false)}
      />
    </Layout>
  );
};
